import FormFeedback from './FormFeedback';
import { useFormFeedback } from '../hooks/useFormFeedback';
import { required, emailError, getApiProblem } from '../utils/form-feedback';
import { css } from '@emotion/react';
import { Card, TextField, Typography } from '@mui/material';
import FilledButton from './buttons/FilledButton';
import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
/** @jsxImportSource @emotion/react */

const styles = {
  card: css`
    width: 100%;
    height: 100%;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  textField: css`
    width: 300px;
  `,
};

export default function LoginCard() {
  const feedback = useFormFeedback();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [resettingPassword, setResettingPassword] = useState(false);

  const [password2, setPassword2] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');

  const [user, setUser] = useState<any>();

  const navigate = useNavigate();

  async function signIn() {
    if (!feedback.validate({ email: emailError(email), password: required(password, 'Password') })) return;
    await feedback.execute(async () => {
      const u = await Auth.signIn({ username: email.trim(), password });
      if (u?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUser(u);
        setResettingPassword(true);
        setPassword('');
      } else {
        navigate('/events');
      }
    }, authProblem);
  }

  async function resetPassword() {
    if (
      !feedback.validate({
        password2: required(password2, 'New password'),
        passwordRepeat:
          required(passwordRepeat, 'Repeat password') ||
          (password2 !== passwordRepeat ? 'Passwords do not match.' : ''),
      })
    )
      return;
    await feedback.execute(async () => {
      const auth = await Auth.completeNewPassword(user, password2);
      navigate('/events');
    }, authProblem);
  }

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => navigate('/events'))
      .catch(() => {});
  }, []);

  return (
    <Card css={styles.card} elevation={4}>
      <FormFeedback message={feedback.message} errors={feedback.errors} />
      {!resettingPassword ? (
        <>
          <TextField
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            css={styles.textField}
            sx={{ marginBottom: '24px' }}
            label="Email Address"
            required
            {...feedback.fieldProps('email')}
          />
          <TextField
            css={styles.textField}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ marginBottom: '48px' }}
            label="Password"
            required
            {...feedback.fieldProps('password')}
            type="password"
          />
          <FilledButton
            disabled={feedback.busy}
            onClick={() => signIn()}
            size="large"
            sx={{ fontSize: '20px', width: '220px' }}
          >
            Login
          </FilledButton>
        </>
      ) : (
        <>
          <Typography sx={{ marginBottom: 4 }} variant="body2">
            Please enter a new password.
          </Typography>
          <TextField
            css={styles.textField}
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            sx={{ marginBottom: '48px' }}
            label="Password"
            required
            {...feedback.fieldProps('password2')}
            type="password"
          />
          <TextField
            css={styles.textField}
            value={passwordRepeat}
            onChange={(e) => setPasswordRepeat(e.target.value)}
            sx={{ marginBottom: '48px' }}
            label="Password Repeat"
            required
            {...feedback.fieldProps('passwordRepeat')}
            type="password"
          />
          <FilledButton
            disabled={feedback.busy}
            onClick={() => resetPassword()}
            size="large"
            sx={{ fontSize: '20px', width: '220px' }}
          >
            Confirm
          </FilledButton>
        </>
      )}
    </Card>
  );
}

function authProblem(error: unknown) {
  const code = (error as { code?: string })?.code;
  if (code === 'NotAuthorizedException' || code === 'UserNotFoundException')
    return 'We could not sign you in with that email and password. Please check them and try again.';
  if (code === 'UserNotConfirmedException')
    return 'Your account is not ready to sign in. Please contact an administrator.';
  if (code === 'InvalidPasswordException')
    return 'Your new password does not meet the password requirements. Choose a stronger password and try again.';
  if (code === 'LimitExceededException' || code === 'TooManyRequestsException')
    return 'Too many attempts were made. Please wait a moment and try again.';
  if (code === 'NetworkError')
    return 'We could not connect to the sign-in service. Check your internet connection and try again.';
  return getApiProblem(error).message;
}
